import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiUploadMultiple, apiDelete } from '../services/api';
import {
  Landmark,
  Upload,
  ChevronLeft,
  ChevronRight,
  Trash2,
  FileText,
  List,
} from 'lucide-react';

interface BankStatement {
  id: string;
  account_name: string | null;
  account_number: string | null;
  bank_name: string;
  currency: string;
  statement_period_start: string | null;
  statement_period_end: string | null;
  opening_balance: number | null;
  closing_balance: number | null;
  transaction_count: number;
  original_filename: string | null;
  status: string;
  uploaded_at: string | null;
}

interface PaginatedResponse {
  items: BankStatement[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface DedupSummary {
  inserted: number;
  skipped: number;
  skipped_descriptions: string[];
}

interface UploadResult {
  filename: string;
  success: boolean;
  error: string | null;
  dedup_summary: DedupSummary | null;
}

interface BatchUploadResponse {
  results: UploadResult[];
}

const statusColors: Record<string, string> = {
  processed: 'bg-blue-500/10 text-blue-400',
  review_pending: 'bg-yellow-500/10 text-yellow-400',
  imported: 'bg-green-500/10 text-green-400',
  failed: 'bg-red-500/10 text-red-400',
};

function formatCurrency(amount: number | null, currency = 'ZAR'): string {
  if (amount === null || amount === undefined) return '-';
  const symbols: Record<string, string> = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || 'R';
  return `${symbol}${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA');
}

export default function BankStatements() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const page = parseInt(searchParams.get('page') || '1');
  const statusFilter = searchParams.get('status') || '';

  const fetchStatements = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '20');
      if (statusFilter) params.set('status', statusFilter);

      const data = await apiGet<PaginatedResponse>(`/api/bank-statements?${params}`);
      setStatements(data.items);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch bank statements:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatements();
  }, [page, statusFilter]);

  const handleStatusFilter = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (newStatus) {
      params.set('status', newStatus);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    const nonPdf = files.filter((f) => !f.name.toLowerCase().endsWith('.pdf'));
    if (nonPdf.length > 0) {
      alert('Please select only PDF files');
      return;
    }

    setIsUploading(true);
    setUploadSummary(null);
    try {
      const data = await apiUploadMultiple<BatchUploadResponse>('/api/bank-statements/upload', files);

      // Build summary message
      const successful = data.results.filter((r) => r.success);
      const failed = data.results.filter((r) => !r.success);
      const totalInserted = successful.reduce((sum, r) => sum + (r.dedup_summary?.inserted || 0), 0);
      const totalSkipped = successful.reduce((sum, r) => sum + (r.dedup_summary?.skipped || 0), 0);

      const parts: string[] = [];
      if (successful.length > 0) {
        parts.push(`${successful.length} statement${successful.length > 1 ? 's' : ''} uploaded`);
        parts.push(`${totalInserted} transactions inserted`);
        if (totalSkipped > 0) {
          parts.push(`${totalSkipped} duplicate${totalSkipped > 1 ? 's' : ''} skipped`);
        }
      }
      if (failed.length > 0) {
        parts.push(`${failed.length} failed: ${failed.map((f) => f.error || f.filename).join(', ')}`);
      }

      setUploadSummary(parts.join('. ') + '.');
      fetchStatements();
    } catch (err: any) {
      alert(err.message || 'Failed to upload bank statements');
      console.error('Failed to upload bank statements:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiDelete(`/api/bank-statements/${id}`);
      setDeleteConfirm(null);
      fetchStatements();
    } catch (err: any) {
      alert(err.message || 'Failed to delete bank statement');
    }
  };

  const statusFilters = ['', 'processed', 'imported', 'failed'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Bank Statements</h1>
          <p className="text-gray-400">{total} imported statements</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/bank-statements/transactions"
            className="flex items-center gap-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-gray-300 hover:text-white rounded-lg transition-colors"
          >
            <List className="w-5 h-5" />
            All Transactions
          </Link>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Upload className="w-5 h-5" />
            {isUploading ? 'Processing...' : 'Upload Statements'}
          </button>
        </div>
      </div>

      {/* Upload Summary */}
      {uploadSummary && (
        <div className="bg-dark-900 rounded-xl border border-dark-800 p-4 flex items-center justify-between">
          <p className="text-sm text-gray-300">{uploadSummary}</p>
          <button
            onClick={() => setUploadSummary(null)}
            className="text-gray-500 hover:text-gray-300 text-sm ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {statusFilters.map((s) => (
          <button
            key={s}
            onClick={() => handleStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
              statusFilter === s
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-gray-400 hover:text-white'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-800">
                <th className="text-left p-4 text-sm font-medium text-gray-400">Statement</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Account</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Period</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Transactions</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Opening</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Closing</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-800">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : statements.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <Landmark className="w-12 h-12 text-gray-600" />
                      <p>No bank statements uploaded yet</p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-primary-500 hover:text-primary-400"
                      >
                        Upload your first ABSA statement
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                statements.map((stmt) => (
                  <tr key={stmt.id} className="hover:bg-dark-800/50">
                    <td className="p-4">
                      <Link
                        to={`/admin/bank-statements/${stmt.id}`}
                        className="flex items-center gap-2 text-white hover:text-primary-500"
                      >
                        <FileText className="w-4 h-4" />
                        <div>
                          <p className="text-sm">{stmt.original_filename || 'Statement'}</p>
                          <p className="text-xs text-gray-500">
                            {stmt.uploaded_at ? formatDate(stmt.uploaded_at) : ''}
                          </p>
                        </div>
                      </Link>
                    </td>
                    <td className="p-4 text-gray-300 text-sm">
                      <div>
                        <p>{stmt.account_name || stmt.bank_name}</p>
                        {stmt.account_number && (
                          <p className="text-xs text-gray-500">***{stmt.account_number.slice(-4)}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-gray-400 text-sm">
                      {stmt.statement_period_start && stmt.statement_period_end ? (
                        <>
                          {formatDate(stmt.statement_period_start)}
                          <span className="text-gray-600"> - </span>
                          {formatDate(stmt.statement_period_end)}
                        </>
                      ) : '-'}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          statusColors[stmt.status] || statusColors.processed
                        }`}
                      >
                        {stmt.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-right text-white text-sm">
                      {stmt.transaction_count}
                    </td>
                    <td className="p-4 text-right text-gray-300 text-sm">
                      {formatCurrency(stmt.opening_balance, stmt.currency)}
                    </td>
                    <td className="p-4 text-right text-gray-300 text-sm">
                      {formatCurrency(stmt.closing_balance, stmt.currency)}
                    </td>
                    <td className="p-4 text-right">
                      {deleteConfirm === stmt.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(stmt.id)}
                            className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-2 py-1 text-xs bg-dark-700 text-gray-400 rounded hover:bg-dark-600"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(stmt.id)}
                          className="p-2 hover:bg-dark-700 rounded-lg inline-block text-gray-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-dark-800">
            <p className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page - 1).toString());
                  setSearchParams(params);
                }}
                disabled={page <= 1}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page + 1).toString());
                  setSearchParams(params);
                }}
                disabled={page >= totalPages}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
